import React, { useState, useEffect, useRef } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    IconButton,
    Tooltip,
    Button,
    Checkbox,
    Box
} from "@mui/material";
import { TableSortLabel } from "@mui/material";
import {
    getComparator,
    stableSort,
} from "../../../../common-utilities/sortingUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import CommonSearchCriteria from "../../common/CommonSearchCriteria";
import {
    GetOutsourcedAnalysisByCriteria,
    DeleteOutsourcedAnalysis,
    ApproveOutsourcedAnalysis,
    ApproveMultipleOutsourcedAnalysis
} from "../../../services/OutsourcedAnalysisService";
import ViewOutsourcedAnalysis from "./ViewOutsourcedAnalysis";
import CommonLoader from "../../common/CommonLoader";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { outsourcedAnalysisStatusList } from "../../common/statusLists";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const OutsourcedAnalysisTable = () => {


    const columns = [
        { id: "select", label: "" },
        { id: "id", label: "ID" },
        { id: "eliSampleLabId", label: "ELI Id" },
        { id: "analysisByLab", label: "Lab Analysis" },
        { id: "testCode", label: "Test Code" },
        { id: "analyteName", label: "Analyte" },
        { id: "resultConcentration", label: "Result" },
        { id: "reportingLimit", label: "Reporting Limit" },
        { id: "analysisDateTime", label: "Analysis Date" },
        { id: "unit", label: "Unit" },
        { id: "actions", label: "Actions" },
    ];

    const rowsPerPageOptions = [20, 50, 100, 150, 200, 400, 600, 800, 1000];

    const [analysisData, setAnalysisData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isDeleted, setIsDeleted] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
    const [orderBy, setOrderBy] = useState("");
    const [order, setOrder] = useState("asc");
    const [selectedAnalysisIds, setSelectedAnalysisIds] = useState([]);
    const [tableHeight, setTableHeight] = useState(600);

    const tableContainerRef = useRef(null);

    const [searchCriteria, setSearchCriteria] = useState({
        searchText: "",
        orderStatus: "Non-Approved",
        startDate: "",
        endDate: "",
        pageNumber: 0,
        pageSize: 10,
    });

    const navigate = useNavigate();

    // Dynamic table height calculation � same approach as BottleOrdersTable
    useEffect(() => {
        const calculateTableHeight = () => {
            if (tableContainerRef.current) {
                const containerTop = tableContainerRef.current.getBoundingClientRect().top;
                const windowHeight = window.innerHeight;
                // Reserve space for pagination (56px) and bottom padding (20px)
                const availableHeight = windowHeight - containerTop - 76;
                const calculatedHeight = Math.max(300, Math.min(availableHeight, windowHeight - 200));
                setTableHeight(calculatedHeight);
            }
        };

        calculateTableHeight();
        window.addEventListener("resize", calculateTableHeight);
        const timeoutId = setTimeout(calculateTableHeight, 100);

        return () => {
            window.removeEventListener("resize", calculateTableHeight);
            clearTimeout(timeoutId);
        };
    }, [filteredData, selectedAnalysisIds]);

    useEffect(() => {
        fetchAnalysis(searchCriteria);
    }, [isDeleted]);

    const fetchAnalysis = async (searchCriteria) => {
        try {
            setLoading(true);

            const response = await GetOutsourcedAnalysisByCriteria(
                searchCriteria.startDate || "",
                searchCriteria.endDate || "",
                searchCriteria.searchText || "",
                searchCriteria.orderStatus || "Non-Approved"
            );

            let filtered = response.results || [];

            if (searchCriteria.orderStatus === "Approved") {
                filtered = filtered.filter(item => item.approvedAt !== null);
            } else if (searchCriteria.orderStatus === "Non-Approved") {
                filtered = filtered.filter(item => item.approvedAt === null);
            }

            setAnalysisData(filtered);
            setFilteredData(filtered);

        } catch (error) {
            console.error("Error fetching analysis:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleSearchCriteria = async (changedSearchCriteria) => {
        const updatedSearchCriteria = {
            ...searchCriteria,
            startDate: changedSearchCriteria.startDate || "",
            endDate: changedSearchCriteria.endDate || "",
            searchText: changedSearchCriteria.searchText || "",
            orderStatus: changedSearchCriteria.orderStatus,
            pageNumber: 0,
        };

        setSearchCriteria(updatedSearchCriteria);
        fetchAnalysis(updatedSearchCriteria);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleViewClick = (analysis) => setSelectedAnalysis(analysis);

    const handleBackClick = () => setSelectedAnalysis(null);

    const handleDeleteClick = (analysis) => {
        setSelectedAnalysis(analysis);
        setIsDeleteConfirmationOpen(true);
    };

    const handleDeleteConfirmationClose = () => {
        setIsDeleteConfirmationOpen(false);
        setSelectedAnalysis(null);
    };

    const handleDeleteConfirmationConfirm = async () => {
        try {
            setLoading(true);

            const response = await DeleteOutsourcedAnalysis(selectedAnalysis.id);

            if (response.results === true) {
                NotificationManager.success("Analysis deleted successfully", "Success");
                setIsDeleted(!isDeleted);
            } else {
                NotificationManager.error("Error deleting analysis", "Error");
            }

        } catch (error) {
            NotificationManager.error("Error deleting analysis", "Error");
        } finally {
            setLoading(false);
            setSelectedAnalysis(null);
            setIsDeleteConfirmationOpen(false);
        }
    };

    const handleApprove = async (analysisId) => {
        try {
            setLoading(true);

            const response = await ApproveOutsourcedAnalysis(analysisId);

            if (response.results === true) {
                NotificationManager.success("Analysis approved successfully", "Success");
                setIsDeleted(!isDeleted);
            } else {
                NotificationManager.error("Error approving analysis", "Error");
            }

        } catch (error) {
            NotificationManager.error("Error approving analysis", "Error");
        } finally {
            setLoading(false);
        }
    };

    const handleApproveMultiple = async () => {
        try {
            setLoading(true);

            const response = await ApproveMultipleOutsourcedAnalysis(selectedAnalysisIds);

            if (response.results === true) {
                NotificationManager.success("Analyses approved successfully", "Success");
                setSelectedAnalysisIds([]);
                setIsDeleted(!isDeleted);
            } else {
                NotificationManager.error("Error approving analyses", "Error");
            }

        } catch (error) {
            NotificationManager.error("Error approving analyses", "Error");
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (id) => {
        setSelectedAnalysisIds((prev) =>
            prev.includes(id)
                ? prev.filter((item) => item !== id)
                : [...prev, id]
        );
    };

    const handleEditAnalysisClick = (analysis) =>
        navigate(`/outsourced-analysis/${analysis.id}`);

    const renderActions = (analysis) => (
        <div style={{ minWidth: "120px", display: "flex" }}>
            {!analysis.approvedAt && (
                <Tooltip title="Approve and sync external analysis results to internal order results">
                    <IconButton
                        style={{ color: "#cf4e6eff" }}
                        onClick={() => handleApprove(analysis.id)}
                    >
                        <CheckCircleIcon />
                    </IconButton>
                </Tooltip>
            )}

            <Tooltip title="Edit Analysis">
                <IconButton
                    style={{ color: "orange" }}
                    onClick={() => handleEditAnalysisClick(analysis)}
                >
                    <EditIcon />
                </IconButton>
            </Tooltip>

            <Tooltip title="View Analysis">
                <IconButton
                    style={{ color: "green" }}
                    onClick={() => handleViewClick(analysis)}
                >
                    <VisibilityIcon />
                </IconButton>
            </Tooltip>

            <Tooltip title="Delete Analysis">
                <IconButton
                    style={{ color: "red" }}
                    onClick={() => handleDeleteClick(analysis)}
                >
                    <DeleteIcon />
                </IconButton>
            </Tooltip>
        </div>
    );

    return (
        <>
            <span className="title">Outsourced Analysis</span>
            <hr />

            {selectedAnalysis ? (
                <>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBackClick}
                        style={{ marginBottom: "16px", color: "green" }}
                    >
                        Back
                    </Button>

                    <ViewOutsourcedAnalysis analysis={selectedAnalysis} />
                </>
            ) : (
                <>
                    <div style={{ marginBottom: "20px" }}>
                        <CommonSearchCriteria
                            orderStatusOptions={outsourcedAnalysisStatusList}
                            onSearch={handleSearchCriteria}
                        />
                    </div>

                    {selectedAnalysisIds.length > 0 && (
                        <div style={{ marginBottom: "10px", textAlign: "right", marginRight: "30px" }}>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleApproveMultiple}
                            >
                                Approve ({selectedAnalysisIds.length})
                            </Button>
                        </div>
                    )}

                    <Paper>
                        <TableContainer
                            ref={tableContainerRef}
                            sx={{ maxHeight: tableHeight, overflowY: "auto", width: "99%" }}
                        >
                            <Table size="small" stickyHeader>

                                <TableHead
                                    sx={{
                                        backgroundColor: (theme) => theme.palette.primary.main,
                                        color: "white",
                                    }}
                                >
                                    <TableRow>

                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={
                                                    selectedAnalysisIds.length > 0 &&
                                                    selectedAnalysisIds.length < filteredData.length
                                                }
                                                checked={
                                                    filteredData.length > 0 &&
                                                    selectedAnalysisIds.length === filteredData.length
                                                }
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedAnalysisIds(filteredData.map(a => a.id));
                                                    } else {
                                                        setSelectedAnalysisIds([]);
                                                    }
                                                }}
                                                style={{ color: "white" }}
                                            />
                                        </TableCell>

                                        {columns.slice(1).map((col) => (
                                            <TableCell key={col.id}>
                                                {col.id === "actions" ? col.label :
                                                    <TableSortLabel
                                                        active={orderBy === col.id}
                                                        direction={orderBy === col.id ? order : "asc"}
                                                        onClick={() => handleRequestSort(col.id)}
                                                    >
                                                        {col.label}
                                                    </TableSortLabel>
                                                }
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>

                                    {stableSort(filteredData, getComparator(order, orderBy))
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((analysis, index) => (

                                            <TableRow
                                                key={analysis.id}
                                                sx={{
                                                    backgroundColor:
                                                        index % 2 === 0 ? "rgba(0,0,0,0.04)" : "white"
                                                }}
                                            >

                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedAnalysisIds.includes(analysis.id)}
                                                        onChange={() => handleCheckboxChange(analysis.id)}
                                                    />
                                                </TableCell>

                                                {columns.slice(1, -1).map((col) => (
                                                    <TableCell key={col.id}>
                                                        {col.id === "analysisDateTime" && analysis[col.id]
                                                            ? new Date(analysis[col.id]).toLocaleString([], {
                                                                year: "numeric",
                                                                month: "short",
                                                                day: "2-digit",
                                                                hour: "2-digit",
                                                                minute: "2-digit"
                                                            })
                                                            : analysis[col.id] || "N/A"}
                                                    </TableCell>
                                                ))}

                                                <TableCell>{renderActions(analysis)}</TableCell>

                                            </TableRow>

                                        ))}

                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={rowsPerPageOptions}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />

                    </Paper>
                </>
            )}

            <DeleteConfirmation
                isOpen={isDeleteConfirmationOpen}
                onClose={handleDeleteConfirmationClose}
                onConfirm={handleDeleteConfirmationConfirm}
                message="Are you sure you want to delete this outsourced analysis?"
            />

            <CommonLoader loading={loading} />
        </>
    );


};

export default OutsourcedAnalysisTable;
